import { Cpu, Gauge, Factory, Layers, Rocket, type LucideIcon } from 'lucide-react';
import Slide from '@/components/shared/Slide';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { RoadmapCompanyConfig, ViewMode } from '@/types/roadmap.types';

interface Props {
  config: RoadmapCompanyConfig;
  mode: ViewMode;
}

const DEFAULT_ICONS: Record<string, LucideIcon> = {
  rd: Cpu,
  testing: Gauge,
  manufacturing: Factory,
  prototyping: Factory,
  general: Layers,
  commercial: Rocket,
  deployment: Rocket,
  scaling: Rocket,
};

const ICON_MAP: Record<string, LucideIcon> = {
  Cpu,
  Gauge,
  Factory,
  Layers,
  Rocket,
};

function resolveIcon(layerId: string, iconOverride?: string): LucideIcon {
  if (iconOverride && ICON_MAP[iconOverride]) return ICON_MAP[iconOverride];
  return DEFAULT_ICONS[layerId] ?? Layers;
}

export default function IntroductionSlide({ config, mode }: Props) {
  const { company, investment_layers } = config;

  const intro = config.intro_statement
    ?? `This roadmap maps funding opportunities for ${company.name} across ${investment_layers.length} investment layers to build a realistic path to external funding over the next 12\u201318 months.`;

  const count = investment_layers.length;
  const gridCols = count <= 2 || count === 4
    ? 'md:grid-cols-2 print:grid-cols-2'
    : 'md:grid-cols-3 print:grid-cols-3';

  return (
    <Slide mode={mode} wide>
      <div className="w-full max-w-6xl mx-auto print:max-w-none flex flex-col items-center justify-center min-h-[60vh] print:min-h-0 print:justify-start">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-3">
          Introduction &amp; Purpose
        </p>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4 print:mb-2 print:text-[16pt] text-center">
          What this roadmap presents
        </h2>
        <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-8 print:mb-4 print:text-[10pt] text-center">
          {intro}
        </p>

        <div className={`grid grid-cols-1 ${gridCols} gap-6 md:gap-8 w-full max-w-5xl mx-auto print:gap-2 print:max-w-none`}>
          {investment_layers.map((layer) => {
            const Icon = resolveIcon(layer.id, layer.icon);
            return (
              <Card
                key={layer.id}
                className="min-w-0 rounded-lg border border-primary/20 shadow-md print:shadow-sm print:p-0 print:break-inside-avoid"
              >
                <CardHeader className="pb-4 print:pb-2 print:pt-4 print:px-4">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4 print:w-11 print:h-11 print:mb-2 bg-primary/10">
                    <Icon className="w-7 h-7 text-primary print:w-5 print:h-5" aria-hidden />
                  </div>
                  <CardTitle className="text-lg md:text-xl font-bold tracking-tight text-foreground print:text-[11pt]">
                    {layer.label}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pb-6 print:pb-3 print:px-4">
                  <p className="text-sm md:text-base leading-relaxed text-muted-foreground print:text-[8pt]">
                    {layer.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </Slide>
  );
}
